import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  limit,
  query,
  where,
} from "firebase/firestore";
import { useParams } from "react-router-dom";
import { db } from "../../firebase/config";
import {
  PRODUCT_AVAILABILITY,
  getProductAvailabilityLabel,
  getProductAvailabilityStatus,
} from "../../constants/availability";
import { MARKETPLACE_SECTIONS } from "../../constants/plantsGarden";
import {
  formatAvailability,
  formatProductPrice,
} from "../../utils/productDisplay";
import {
  getBroadStorefrontLocation,
  getPublicGalleryImages,
  getPublicStorefrontBadges,
  getPublicStorefrontName,
  getStorefrontAvatarFrameAsset,
  getStorefrontDecorationLevel,
  getStorefrontTheme,
  getStorefrontThemeConfig,
  isActivePublicStorefrontProduct,
  normalizeStorefrontSlug,
} from "../../utils/storefront";
import { PUBLIC_STOREFRONT_COLLECTION } from "../../utils/publicStorefront";
import FarmAvatar from "../Common/FarmAvatar";
import "./PublicFarmProfile.css";

const PRODUCT_OWNER_FIELDS = ["userId", "ownerId", "sellerId"];
const MAX_PRODUCT_QUERY_RESULTS = 80;

const getBadgeClassName = (label = "") =>
  String(label || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const normalizeSlug = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/['"’‘]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const dedupeById = (items = []) => {
  const seen = new Set();
  return items.filter((item) => {
    if (!item?.id || seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
};

const findPublicProfileBySlug = async (slug) => {
  const cleanSlug = normalizeStorefrontSlug(slug);
  if (!cleanSlug) return { profile: null, ambiguous: false };

  const snapshot = await getDoc(
    doc(db, PUBLIC_STOREFRONT_COLLECTION, cleanSlug)
  );
  if (!snapshot.exists()) return { profile: null, ambiguous: false };

  const profile = { id: snapshot.data().ownerId || "", ...snapshot.data() };
  if (
    profile.slug !== cleanSlug ||
    profile.storefrontEnabled !== true ||
    profile.isPublic !== true ||
    profile.status !== "active"
  ) {
    return { profile: null, ambiguous: false };
  }

  return { profile, ambiguous: false };
};

const fetchPublicProductsForUser = async (userId) => {
  const productResults = [];

  for (const field of PRODUCT_OWNER_FIELDS) {
    const snapshot = await getDocs(
      query(
        collection(db, "products"),
        where(field, "==", userId),
        where("status", "==", "active"),
        limit(MAX_PRODUCT_QUERY_RESULTS)
      )
    );
    snapshot.forEach((docSnap) => {
      productResults.push({ id: docSnap.id, ...docSnap.data() });
    });
  }

  return dedupeById(productResults)
    .filter(isActivePublicStorefrontProduct)
    .sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() || 0;
      const bTime = b.createdAt?.toMillis?.() || 0;
      return bTime - aTime;
    });
};

const PublicProductCard = ({ product }) => {
  const availability = getProductAvailabilityStatus(product);
  const section =
    product.marketplaceSection === MARKETPLACE_SECTIONS.PLANTS_GARDEN
      ? "Plants & Garden"
      : "Farm Market";

  return (
    <article className="public-storefront-product-card">
      <div className="public-storefront-product-image">
        {product.image ? (
          <img src={product.image} alt={product.name || "Product listing"} loading="lazy" />
        ) : (
          <span>MyFarmingPal</span>
        )}
      </div>

      <div className="public-storefront-product-body">
        <div className="public-storefront-product-labels">
          <span
            className={`public-storefront-product-label public-storefront-product-label-${getBadgeClassName(
              section
            )}`}
          >
            {section}
          </span>
          <span
            className={`public-storefront-product-label public-storefront-product-label-${getBadgeClassName(
              getProductAvailabilityLabel(availability)
            )}`}
          >
            {getProductAvailabilityLabel(availability)}
          </span>
        </div>
        <h3>{product.name || "Farm Product"}</h3>
        <p className="public-storefront-product-price">{formatProductPrice(product)}</p>
        <p className="public-storefront-product-availability">
          Available:{" "}
          <strong>
            {formatAvailability(
              product.quantityAvailable ?? product.quantity ?? 1,
              product.unit
            )}
          </strong>
        </p>
        {product.description && (
          <p className="public-storefront-product-description">{product.description}</p>
        )}
      </div>
    </article>
  );
};

const PublicFarmProfile = () => {
  const { slug } = useParams();
  const [profile, setProfile] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const originalTitle = document.title;

    const loadProfile = async () => {
      setLoading(true);
      setUnavailable(false);

      try {
        const result = await findPublicProfileBySlug(slug);
        if (!isMounted) return;

        if (!result.profile || result.ambiguous) {
          setProfile(null);
          setProducts([]);
          setUnavailable(true);
          document.title = "Profile unavailable | MyFarmingPal";
          return;
        }

        const publicProducts = await fetchPublicProductsForUser(result.profile.id);
        if (!isMounted) return;

        setProfile(result.profile);
        setProducts(publicProducts);
        document.title = `${getPublicStorefrontName(result.profile)} | MyFarmingPal`;
      } catch (error) {
        console.error("Error loading public farm profile:", error);
        if (isMounted) {
          setProfile(null);
          setProducts([]);
          setUnavailable(true);
          document.title = "Profile unavailable | MyFarmingPal";
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadProfile();

    return () => {
      isMounted = false;
      document.title = originalTitle;
    };
  }, [slug]);

  const galleryImages = useMemo(() => {
    return getPublicGalleryImages(profile || {}, 12);
  }, [profile]);

  const farmMarketProducts = products.filter(
    (product) =>
      (product.marketplaceSection || MARKETPLACE_SECTIONS.GENERAL) !==
      MARKETPLACE_SECTIONS.PLANTS_GARDEN
  );
  const plantProducts = products.filter(
    (product) =>
      product.marketplaceSection === MARKETPLACE_SECTIONS.PLANTS_GARDEN
  );

  if (loading) {
    return (
      <main className="public-storefront-page">
        <section className="public-storefront-shell">
          <p className="public-storefront-loading">Loading public profile...</p>
        </section>
      </main>
    );
  }

  if (unavailable || !profile) {
    return (
      <main className="public-storefront-page">
        <section className="public-storefront-shell public-storefront-empty-page">
          <h1>Farm storefront not found</h1>
          <p>This public farm profile is not available, may be private, or may have moved.</p>
        </section>
      </main>
    );
  }

  const publicName = getPublicStorefrontName(profile);
  const broadLocation = getBroadStorefrontLocation(profile);
  const aboutText = profile.about || profile.bio || profile.intro || "";
  const storefrontTheme = getStorefrontTheme(profile);
  const storefrontThemeConfig = getStorefrontThemeConfig(storefrontTheme);
  const storefrontAvatarFrameAsset = getStorefrontAvatarFrameAsset(
    profile,
    storefrontThemeConfig
  );
  const storefrontDecorationLevel = getStorefrontDecorationLevel(profile);
  const storefrontThemeStyle = {
    "--public-storefront-bg-image": `url(${storefrontThemeConfig.background})`,
    "--public-storefront-hero-image": `url(${storefrontThemeConfig.desktopHero})`,
    "--public-storefront-mobile-hero-image": `url(${storefrontThemeConfig.mobileHero})`,
    "--public-storefront-product-bg": `url(${storefrontThemeConfig.productCardBg})`,
    "--public-storefront-avatar-frame": `url(${storefrontAvatarFrameAsset})`,
    "--public-storefront-divider": `url(${storefrontThemeConfig.divider})`,
    "--public-storefront-decoration-primary": `url(${storefrontThemeConfig.decorationPrimary})`,
    "--public-storefront-decoration-secondary": `url(${storefrontThemeConfig.decorationSecondary})`,
    "--public-storefront-decoration-tertiary": `url(${storefrontThemeConfig.decorationTertiary})`,
    "--public-storefront-badge-available": `url(${storefrontThemeConfig.badgeAssets.availableNow})`,
    "--public-storefront-badge-farm": `url(${storefrontThemeConfig.badgeAssets.farmFresh})`,
    "--public-storefront-badge-handmade": `url(${storefrontThemeConfig.badgeAssets.handmade})`,
    "--public-storefront-badge-made": `url(${storefrontThemeConfig.badgeAssets.madeToOrder})`,
    "--public-storefront-badge-plants": `url(${storefrontThemeConfig.badgeAssets.plants})`,
    "--public-storefront-icon-star": `url(${storefrontThemeConfig.iconAssets.badgeStar})`,
    "--public-storefront-icon-check": `url(${storefrontThemeConfig.iconAssets.checkCircle})`,
    "--public-storefront-ui-frame": `url(${storefrontThemeConfig.uiAssets.selectedCardBorder})`,
    "--public-storefront-ui-indicator": `url(${storefrontThemeConfig.uiAssets.unselectedIndicator})`,
    "--public-storefront-accent": storefrontThemeConfig.palette.accent,
    "--public-storefront-accent-dark": storefrontThemeConfig.palette.accentDark,
    "--public-storefront-paper": storefrontThemeConfig.palette.paper,
    "--public-storefront-wash": storefrontThemeConfig.palette.wash,
  };
  const publicBadges = getPublicStorefrontBadges({
    profile,
    products,
    galleryImages,
  });

  return (
    <main
      className={`public-storefront-page public-storefront-theme-${storefrontTheme} public-storefront-decoration-${storefrontDecorationLevel}`}
      style={storefrontThemeStyle}
    >
      <section className="public-storefront-shell">
        <header className="public-storefront-hero">
          <div className="public-storefront-profile-card">
            <p className="public-storefront-eyebrow">Public MyFarmingPal profile</p>

            <div className="public-storefront-avatar-frame">
              <FarmAvatar
                profile={{ ...profile, name: publicName }}
                className="public-storefront-avatar"
              />
            </div>

            <h1>{publicName}</h1>
            {broadLocation && <p className="public-storefront-location">{broadLocation}</p>}
            {aboutText && <p className="public-storefront-hero-about">{aboutText}</p>}
            {publicBadges.length > 0 && (
              <div className="public-storefront-badges">
                {publicBadges.map((badge) => (
                  <span key={badge.key} className="public-storefront-badge">
                    {badge.icon ? `${badge.icon} ` : ""}
                    {badge.label}
                  </span>
                ))}
              </div>
            )}

            <div className="public-storefront-actions" aria-label="Public storefront actions">
              <a href="#public-storefront-products">Browse listings</a>
              {galleryImages.length > 0 && <a href="#public-storefront-gallery">View gallery</a>}
            </div>
          </div>
        </header>

        <div className="public-storefront-divider" aria-hidden="true" />

        <section className="public-storefront-section public-storefront-about-section">
          <h2>About</h2>
          {aboutText ? (
            <p className="public-storefront-about">{aboutText}</p>
          ) : (
            <p className="public-storefront-muted">No public about information yet.</p>
          )}
        </section>

        <section className="public-storefront-section public-storefront-gallery-section" id="public-storefront-gallery">
          <h2>Farm Work / Gallery</h2>
          {galleryImages.length > 0 ? (
            <div className="public-storefront-gallery">
              {galleryImages.map((image, index) => (
                <img
                  key={`${image.url}-${index}`}
                  src={image.url}
                  alt={`${publicName} farm work`}
                  loading="lazy"
                />
              ))}
            </div>
          ) : (
            <p className="public-storefront-muted">No farm work photos yet.</p>
          )}
        </section>

        <div className="public-storefront-divider public-storefront-divider-products" aria-hidden="true" />

        <section className="public-storefront-section public-storefront-products-section" id="public-storefront-products">
          <div className="public-storefront-section-heading">
            <h2>Featured Public Listings</h2>
            <span>{products.length} public listing{products.length === 1 ? "" : "s"}</span>
          </div>

          {products.length === 0 ? (
            <p className="public-storefront-muted">No public products yet.</p>
          ) : (
            <>
              {farmMarketProducts.length > 0 && (
                <div className="public-storefront-product-group">
                  <h3>Farm Market</h3>
                  <div className="public-storefront-products">
                    {farmMarketProducts.map((product) => (
                      <PublicProductCard key={product.id} product={product} />
                    ))}
                  </div>
                </div>
              )}

              {plantProducts.length > 0 && (
                <div className="public-storefront-product-group">
                  <h3>Plants & Garden</h3>
                  <div className="public-storefront-products">
                    {plantProducts.map((product) => (
                      <PublicProductCard key={product.id} product={product} />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </section>
    </main>
  );
};

export default PublicFarmProfile;
